import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  PhotoCamera as CameraIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { TryOnImageProcessor } from '../../utils/tryOnImageProcessor';
import profilePictureService, { SecureProfilePictureResponse } from '../../services/profile-picture-service';
import { logger } from '../../utils/logger';

interface ProfilePictureUploadProps {
  currentImageUrl?: string;
  onUploadSuccess: (imageUrl: string) => void;
  onUploadError: (error: string) => void;
  onDelete: () => void;
}

interface ProcessingStatus {
  isProcessing: boolean;
  progress: number;
  originalSize?: number;
  processedSize?: number;
  error?: string;
  warnings?: string[];
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  currentImageUrl,
  onUploadSuccess,
  onUploadError,
  onDelete,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [secureImageUrl, setSecureImageUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>({
    isProcessing: false,
    progress: 0,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingSecureUrl, setIsLoadingSecureUrl] = useState(false);

  // Use try-on image processor constants
  const MAX_FILE_SIZE = TryOnImageProcessor.MAX_FILE_SIZE;
  const ALLOWED_FORMATS = TryOnImageProcessor.ALLOWED_FORMATS;
  const MIN_DIMENSIONS = { width: TryOnImageProcessor.MIN_WIDTH, height: TryOnImageProcessor.MIN_HEIGHT };
  const TARGET_DIMENSIONS = { width: TryOnImageProcessor.TARGET_WIDTH, height: TryOnImageProcessor.TARGET_HEIGHT };

  // Load secure URL when currentImageUrl changes
  React.useEffect(() => {
    const loadSecureUrl = async () => {
      if (currentImageUrl && !previewUrl) {
        setIsLoadingSecureUrl(true);
        try {
          logger.info('Fetching secure profile picture URL');
          const response = await profilePictureService.getSecureProfilePictureUrl();
          setSecureImageUrl(response.signedUrl);
          logger.info('Secure profile picture URL loaded successfully');
        } catch (error: any) {
          logger.error('Error loading secure profile picture URL:', error);
          // If we can't get signed URL, clear the secure URL
          setSecureImageUrl(null);
        } finally {
          setIsLoadingSecureUrl(false);
        }
      } else if (!currentImageUrl) {
        // Clear secure URL if no current image
        setSecureImageUrl(null);
      }
    };

    loadSecureUrl();
  }, [currentImageUrl, previewUrl]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      
      // Validate the file for try-on use
      const validation = await TryOnImageProcessor.validateForTryOn(selectedFile);
      if (!validation.isValid) {
        setUploadError(validation.error || 'Invalid image file');
        return;
      }

      setFile(selectedFile);
      setUploadError(null);

      // Create preview URL using the try-on processor
      try {
        const previewUrl = await TryOnImageProcessor.createPreview(selectedFile);
        setPreviewUrl(previewUrl);
      } catch (error) {
        // Fallback to regular preview
        const objectUrl = URL.createObjectURL(selectedFile);
        setPreviewUrl(objectUrl);
      }

      // Set processing status with try-on specific info
      const processingInfo = TryOnImageProcessor.getProcessingInfo(selectedFile);
      setProcessingStatus({
        isProcessing: false,
        progress: 0,
        originalSize: selectedFile.size,
        warnings: validation.warnings,
      });

      logger.info('Try-on image selected', {
        fileName: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
        processingInfo,
        warnings: validation.warnings,
      });
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/webp': [],
    },
    maxSize: MAX_FILE_SIZE,
    multiple: false,
  });

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      // Show compression progress if needed
      const processingInfo = TryOnImageProcessor.getProcessingInfo(file);
      if (processingInfo.willBeCompressed) {
        setProcessingStatus(prev => ({
          ...prev,
          isProcessing: true,
          progress: 25,
        }));
      }

      // Upload the file
      const response = await profilePictureService.uploadProfilePicture(file);

      // Update processing status
      setProcessingStatus(prev => ({
        ...prev,
        isCompressing: false,
        progress: 100,
      }));

      // Reset form state
      setFile(null);
      setPreviewUrl(null);
      setProcessingStatus({
        isProcessing: false,
        progress: 0,
      });

      // Notify parent component with the try-on profile picture URL
      onUploadSuccess(response.profilePictureUrl);

      // Fetch the secure URL for immediate display
      try {
        logger.info('Fetching secure URL after upload');
        const secureResponse = await profilePictureService.getSecureProfilePictureUrl();
        setSecureImageUrl(secureResponse.signedUrl);
        logger.info('Secure URL loaded after upload');
      } catch (secureError) {
        logger.warn('Could not fetch secure URL after upload:', secureError);
      }

      logger.info('Profile picture uploaded successfully');
    } catch (error: any) {
      logger.error('Profile picture upload failed:', error);
      setUploadError(error.message || 'Failed to upload profile picture');
      setProcessingStatus(prev => ({
        ...prev,
        isProcessing: false,
        error: 'Upload failed',
      }));
      onUploadError(error.message || 'Failed to upload profile picture');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    
    try {
      await profilePictureService.deleteProfilePicture();
      setDeleteDialogOpen(false);
      setSecureImageUrl(null); // Clear secure URL
      onDelete();
      logger.info('Profile picture deleted successfully');
    } catch (error: any) {
      logger.error('Profile picture deletion failed:', error);
      setUploadError(error.message || 'Failed to delete profile picture');
    } finally {
      setIsDeleting(false);
    }
  };

  const clearSelection = () => {
    setFile(null);
    setPreviewUrl(null);
    setUploadError(null);
    setProcessingStatus({
      isProcessing: false,
      progress: 0,
    });
  };

  return (
    <Paper elevation={2} sx={{ p: 3, maxWidth: 500 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <CameraIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          Try-On Profile Picture
        </Typography>
        <Tooltip title="This picture is used for virtual try-on and is only visible to you">
          <IconButton size="small" sx={{ ml: 1 }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a personal photo to use for virtual try-on. This image will be processed to {TARGET_DIMENSIONS.width}x{TARGET_DIMENSIONS.height} pixels and is private to your account.
      </Typography>

      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {uploadError}
        </Alert>
      )}

      {/* Current Profile Picture Display */}
      {currentImageUrl && !previewUrl && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {isLoadingSecureUrl ? (
            <Box sx={{ width: 80, height: 160, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2, bgcolor: 'grey.200' }}>
              <CircularProgress size={30} />
            </Box>
          ) : secureImageUrl ? (
            <Avatar
              src={secureImageUrl}
              alt="Current profile picture"
              sx={{ width: 80, height: 160, mr: 2, borderRadius: 2 }}
              variant="rounded"
            />
          ) : (
            <Box sx={{ width: 80, height: 160, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2, bgcolor: 'grey.300' }}>
              <Typography variant="caption" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Current try-on profile picture
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ mt: 1 }}
              disabled={isLoadingSecureUrl}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}

      {/* File Upload Area */}
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          mb: 2,
          backgroundColor: isDragActive ? 'action.hover' : 'transparent',
          '&:hover': {
            backgroundColor: 'action.hover',
            borderColor: 'primary.main',
          },
        }}
      >
        <input {...getInputProps()} />
        
        {previewUrl ? (
          <Box sx={{ position: 'relative' }}>
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
            <Button
              variant="contained"
              size="small"
              color="primary"
              sx={{ position: 'absolute', top: 10, right: 10 }}
              onClick={(e) => {
                e.stopPropagation();
                clearSelection();
              }}
            >
              Change Image
            </Button>
          </Box>
        ) : (
          <Box>
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              {isDragActive
                ? 'Drop the image here...'
                : 'Drag and drop an image here, or click to select'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supported formats: JPEG, PNG, WebP (max {MAX_FILE_SIZE / (1024 * 1024)}MB)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Minimum size: {MIN_DIMENSIONS.width}x{MIN_DIMENSIONS.height} pixels
            </Typography>
          </Box>
        )}
      </Box>

      {/* Compression Status Display */}
      {file && (processingStatus.originalSize || processingStatus.isProcessing) && (
        <Card sx={{ mb: 2, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Image Processing
            </Typography>
            
            {processingStatus.originalSize && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Original size: {TryOnImageProcessor.formatFileSize(processingStatus.originalSize)}
                </Typography>
                {TryOnImageProcessor.getProcessingInfo(file).willBeCompressed && (
                  <Typography variant="body2" color="warning.main">
                    ⚠️ Large image detected - will be optimized for try-on processing
                  </Typography>
                )}
              </Box>
            )}

            {processingStatus.isProcessing && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" gutterBottom>
                  Processing image...
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={processingStatus.progress} 
                  sx={{ mb: 1 }}
                />
              </Box>
            )}

            {processingStatus.error && (
              <Typography variant="body2" color="error.main">
                ❌ {processingStatus.error}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Button */}
      {file && (
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleUpload}
            disabled={isUploading}
            startIcon={
              isUploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />
            }
            sx={{ minWidth: 200 }}
          >
            {isUploading ? 'Uploading...' : 'Upload Profile Picture'}
          </Button>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Profile Picture</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your try-on profile picture? 
            This will prevent you from using the virtual try-on feature until you upload a new picture.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={
              isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />
            }
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ProfilePictureUpload;